import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  PermissaoUsuarioAtributos,
  PermissaoUsuarioCSS,
  PermissaoUsuarioPorMenuID,
} from '@interfaces/permissao-usuario';
import {
  UsuarioPermissaoEnum,
  UsuarioPermissaoFlags,
} from '@utils/enums/UsuarioPermissaoEnums';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { LoginService } from '@services/login.service';

@Injectable({
  providedIn: 'root',
})
export class UsuarioPermissaoService {
  usuarioPermissoes$ = new BehaviorSubject<PermissaoUsuarioPorMenuID>(null);
  atributosPorPermissoes$ = new BehaviorSubject<any>({});
  cssPorPermissoes$ = new BehaviorSubject<any>({});

  constructor(
    private readonly httpClient: HttpClient,
    private readonly loginService: LoginService
  ) {
    this.carregarPermissoes();
  }

  /**
   * Carrega as permissões do usuário que está logado no sistema e
   * armazena no formato especificado, onde a chave do objeto representa
   * o ID do menu/funcionalidade do sistema e o valor representa as permissões.
   */
  carregarPermissoes() {
    const idCliente = this.loginService.getIdCliente();

    if (!idCliente) {
      this.usuarioPermissoes$.next({});
      this.atualizarAtributosPorPermissoes();
      this.atualizarCssPorPermissoes();
      return this.usuarioPermissoes$;
    }

    const url = `/apiconnect/core/permissions/${idCliente}/users`;
    this.httpClient.get<any>(url).pipe(take(1)).subscribe({
      next: ({ response }) => {
        const usuarioPermissoes = response.reduce((acc, menuItem) => {
          acc[menuItem.menuId] = menuItem;
          return acc;
        }, {});
        this.usuarioPermissoes$.next(usuarioPermissoes);
        this.atualizarAtributosPorPermissoes();
        this.atualizarCssPorPermissoes();
      },
      error: () => {
        this.usuarioPermissoes$.next({});
        this.atualizarAtributosPorPermissoes();
        this.atualizarCssPorPermissoes();
      }
    });
    return this.usuarioPermissoes$;
  }

  /**
   * Verifica se o usuário atual possui permissão
   * para acessar determinada funcionalidade do sistema.
   *
   * @param menuId ID do menu/funcionalidade a ser acessada
   * @param permissao tipo de permissão requisitada (incluir/alterar/etc)
   */
  verificarPermissao(menuId: number, permissao: UsuarioPermissaoFlags) {
    const idCliente = this.loginService.getIdCliente();

    if (!idCliente) {
      // logado com CNPJ, sempre permitir acesso a todos os menus
      return true;
    }

    const chave = [menuId, permissao].join('.');
    const temPermissao = _get(this.usuarioPermissoes$.value, chave, false);
    return temPermissao;
  }

  /**
   * Calcula atributos HTML baseado na permissão que o usuário possui.
   *
   * @param menuId ID do menu/funcionalidade a ser acessada
   * @param permissao tipo de permissão requisitada (incluir/alterar/etc)
   */
  private atualizarAtributosPorPermissoes() {
    const usuarioPermissaoFlagsEntries = Object.entries(UsuarioPermissaoFlags);
    const atributosPorPermissoes = Object.entries(UsuarioPermissaoEnum).reduce(
      (acc, [usuarioPermissaoKey, usuarioPermissao]) => {
        usuarioPermissaoFlagsEntries.forEach(([flagKey, flag]) => {
          const temPermissao = this.verificarPermissao(
            usuarioPermissao.menuId,
            flag
          );
          const atributos: PermissaoUsuarioAtributos = {
            disabled: !temPermissao,
          };
          _set(acc, `${usuarioPermissaoKey}.${flagKey}`, atributos);
        });
        return acc;
      },
      {}
    );
    this.atributosPorPermissoes$.next(atributosPorPermissoes);

    return this.atributosPorPermissoes$;
  }

  /**
   * Calcula classes CSS baseado nas permissões que o usuário possui.
   *
   * @param menuId ID dos menus/funcionalidades a serem acessadas
   * @param permissao tipo de permissão requisitada (incluir/alterar/etc)
   */
  private atualizarCssPorPermissoes() {
    let menuPermissions = {};
    const usuarioPermissaoFlagsEntries = Object.entries(UsuarioPermissaoFlags);
    const cssPorPermissoes = Object.entries(UsuarioPermissaoEnum).reduce(
      (acc, [usuarioPermissaoKey, usuarioPermissao]) => {
        usuarioPermissaoFlagsEntries.forEach(([flagKey, flag]) => {
          const temPermissao = this.verificarPermissao(
            usuarioPermissao.menuId,
            flag
          );
          const atributos: PermissaoUsuarioCSS = {
            disabled: !temPermissao,
            hidden : !temPermissao
          };
          menuPermissions = this.groupMenusToShowOrHideThemFromUser(menuPermissions, 
            usuarioPermissao.menu, 
            temPermissao, 
            flag)

          _set(acc, `${usuarioPermissaoKey}.${flagKey}`, atributos);
        });
        return acc;
      },
      {}
      );
    _set(cssPorPermissoes, "MenuPermissionsCounter", menuPermissions);
    this.cssPorPermissoes$.next(cssPorPermissoes);
    return this.cssPorPermissoes$;
  }

  private groupMenusToShowOrHideThemFromUser(allPermissionsObj : any, menuName : string, hasPermission : boolean, userPermissionLevel : UsuarioPermissaoFlags) {
    if(menuName != null &&
      allPermissionsObj[menuName] == undefined){
        _set(allPermissionsObj, `${menuName}`, null)
    }
    if(hasPermission && userPermissionLevel == UsuarioPermissaoFlags.Imprimir) allPermissionsObj[menuName] += 1;
    return allPermissionsObj;
  }
}
